import React, { useState, useEffect } from 'react';
import styles from './Response.module.scss';
import JsonViewer from '../JsonViewer';
import CustomDropdown from '../CustomDropdown';

const Response = ({ response, error, loading, responseSource, activeExample, api, onExampleSelect, dummyResponseMap }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [dummyResponses, setDummyResponses] = useState([]);

  useEffect(() => {
    if (api && dummyResponseMap) {
      const key = `${api.method.toUpperCase()}${api.path}`;
      const responses = dummyResponseMap[key] || [];
      setDummyResponses(responses);
    } else {
      setDummyResponses([]);
    }
  }, [api, dummyResponseMap]);

  const handleCopy = (data) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const getResponseTitle = () => {
    if (responseSource === 'live') {
      return response.status;
    }
    if (responseSource === 'example' && activeExample) {
      return `${response.status} - ${activeExample.summary || 'Example'}`;
    }
    return response.status;
  };

  const renderContent = () => {
    if (loading) {
      return null;
    }

    if (error) {
      return (
        <div className={styles.responseContainer}>
          <div className={styles.responseHeader}>
            <div>
              <span className={`${styles.statusIndicator} ${styles.error}`}></span>
              <span className={styles.statusText}>{error.status}</span>
            </div>
          </div>
          <div className={styles.responseBody}>
            <JsonViewer json={error.data} />
          </div>
        </div>
      );
    }

    if (response) {
      if (parseInt(response.status, 10) === 204) {
        return (
          <div className={styles.responseContainer}>
            <div className={styles.responseHeader}>
              <div>
                <span className={`${styles.statusIndicator} ${styles.success}`}></span>
                <span className={styles.statusText}>204 - No Content</span>
              </div>
              {responseSource === 'example' && (
                <div className={styles.exampleDropdown}>
                  <CustomDropdown
                    value={activeExample ? `${response.status}|${activeExample.summary}` : ''}
                    options={api && api.responses ? Object.entries(api.responses).map(([status, responseDef]) => ({
                      value: `${status}|${responseDef.description}`,
                      label: status,
                      status: status
                    })) : []}
                    onChange={(option) => {
                      const [status, summary] = option.value.split('|');
                      const responseDef = api.responses[status];
                      if (responseDef) {
                        onExampleSelect(status, {
                          summary: summary,
                          value: responseDef.body || {}
                        });
                      }
                    }}
                  />
                </div>
              )}
            </div>
            <div className={styles.responseBody}>
              <div className={styles.noContentMessage}>
                The server successfully processed the request, but there is no content to return.
              </div>
            </div>
          </div>
        );
      } else {
        return (
          <div className={styles.responseContainer}>
            <div className={styles.responseHeader}>
              <div>
                <span className={`${styles.statusIndicator} ${response.status >= 400 ? styles.error : styles.success}`}></span>
                <span className={styles.statusText}>{getResponseTitle()}</span>
              </div>
              {responseSource === 'example' && (
                <div className={styles.exampleDropdown}>
                  <CustomDropdown
                    value={activeExample ? `${response.status}|${activeExample.summary}` : ''}
                    options={
                      dummyResponses.length > 0
                        ? dummyResponses.map(item => ({
                          value: `${item.statusCode}|${item.reason}`,
                          label: item.statusCode,
                          status: item.statusCode
                        }))
                        : (api && api.responses ? Object.entries(api.responses).map(([status, responseDef]) => ({
                          value: `${status}|${responseDef.description}`,
                          label: status,
                          status: status
                        })) : [])
                    }
                    onChange={(option) => {
                      const [status, summary] = option.value.split('|');
                      if (dummyResponses.length > 0) {
                        const selectedDummy = dummyResponses.find(dr => dr.statusCode === status && dr.reason === summary);
                        if (selectedDummy) {
                          try {
                            const body = JSON.parse(selectedDummy.responseBody);
                            onExampleSelect(status, { summary, value: body });
                          } catch (e) {
                            onExampleSelect(status, { summary, value: selectedDummy.responseBody });
                          }
                        }
                      } else {
                        const responseDef = api.responses[status];
                        if (responseDef) {
                          onExampleSelect(status, {
                            summary: summary,
                            value: responseDef.body || {}
                          });
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
            <div className={styles.responseBody}>
              <button className={styles.copyButton} onClick={() => handleCopy(response.data)}>
                {isCopied ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                )}
              </button>
              <JsonViewer json={response.data} />
            </div>
          </div>
        );
      }
    }

    return (
      <div className={styles.initialState}>
        <p>Click <span className={styles.tryItButton}>Try It!</span> to start a request and see the response here! Or choose an example:</p>
        <div className={styles.exampleLinks}>
          <span>application/json</span>
          <div className={styles.exampleButtonsContainer}>
            {dummyResponses.length > 0
              ? dummyResponses.map(item => (
                <div
                  key={item.statusCode}
                  className={styles.exampleLink}
                  onClick={() => {
                    try {
                      const body = JSON.parse(item.responseBody);
                      onExampleSelect(item.statusCode, { summary: item.reason, value: body });
                    } catch (e) {
                      onExampleSelect(item.statusCode, { summary: item.reason, value: item.responseBody });
                    }
                  }}
                >
                  <span className={`${styles.statusIndicator} ${String(item.statusCode).startsWith('2') ? styles.success : styles.error}`}></span>
                  <span>{item.statusCode}</span>
                </div>
              ))
              : (api && api.responses && Object.entries(api.responses)
                .map(([status, responseDef]) => (
                  <div
                    key={status}
                    className={styles.exampleLink}
                    onClick={() => onExampleSelect(status, {
                      summary: responseDef.description,
                      value: responseDef.body || {}
                    })}
                  >
                    <span className={`${styles.statusIndicator} ${status.startsWith('2') ? styles.success : styles.error}`}></span>
                    <span>{status}</span>
                  </div>
                ))
              )
            }
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.rightPanelSection}>
      <h5 className={styles.rightPanelSectionHeader}>Response</h5>
      {renderContent()}
    </div>
  );
};

export default Response;